import type { Ref } from 'vue';
import type { RoutePlan, RoutingProvider } from '@gpx-spa/geo-domain';
import { yieldUntilSpinnerPainted } from '@gpx-spa/ui';
import { suggestedGeometryQualityDetailFromPaths } from './suggested-geometry-quality';
import { flattenRoutePlanPaths } from './flatten-route-plan-paths';
import {
  evaluateLearnedMatchesAgainstGoogle,
  filterLearnedDuplicatesAgainstGoogle,
  orderMergedSegmentPaths,
} from './filter-learned-duplicates';
import { mergeRoutePlansBySegment } from './merge-route-plans-by-segment';

export type RouteProviderId = 'google' | 'learned';

export type ShipmentRouteProviderErrors = Partial<Record<RouteProviderId, string>>;

export type ShipmentRouteCandidates = {
  google: RoutePlan | null;
  learned: RoutePlan | null;
  errorsByProvider: ShipmentRouteProviderErrors;
};

export type ComputeRouteContext = {
  /** Ordered stop coordinates (origin … destination). At least two required. */
  stopCoordinates: { lat: number; lng: number }[];
  accountId?: number | null;
  googleEnabled?: boolean;
  corridorBufferM?: number;
  learnedGoogleDedup?: {
    enabled: boolean;
    matchThreshold: number;
  };
  onLearnedGoogleMatchMeasured?: (maxMatch: number | null) => void;
  signal?: AbortSignal;
  fetchLearnedPlan?: (signal?: AbortSignal) => Promise<RoutePlan>;
};

export type ComputeRouteDeps = {
  googleRouting: RoutingProvider;
  routePlan: Ref<RoutePlan | null>;
  routeError: Ref<string | null>;
  routeWarning?: Ref<string | null>;
  computingRoute: Ref<boolean>;
  routingLoading: Ref<boolean>;
  geometryQuality: Ref<number>;
  geometryQualityDebounced: Ref<number>;
  corridorBufferMDebounced: Ref<number>;
  commitGeometryQualityImmediately: () => void;
  requestRouteOverview: () => Promise<void>;
};

function isAbortError(error: unknown): boolean {
  if (error == null || typeof error !== 'object') return false;
  const name = (error as { name?: string }).name;
  const code = (error as { code?: string }).code;
  return name === 'AbortError' || code === 'ERR_CANCELED';
}

function providerErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return String(error);
}

export async function requestShipmentRouteCandidates(input: {
  googleRouting?: RoutingProvider;
  googleEnabled: boolean;
  stopCoordinates: { lat: number; lng: number }[];
  fetchLearnedPlan?: (signal?: AbortSignal) => Promise<RoutePlan>;
  signal?: AbortSignal;
}): Promise<ShipmentRouteCandidates> {
  const tasks: Array<{
    provider: RouteProviderId;
    promise: Promise<RoutePlan>;
  }> = [];

  if (input.googleEnabled && input.googleRouting && input.stopCoordinates.length >= 2) {
    tasks.push({
      provider: 'google',
      promise: input.googleRouting.getRoute(input.stopCoordinates, {
        signal: input.signal,
      }),
    });
  }

  if (input.fetchLearnedPlan) {
    tasks.push({
      provider: 'learned',
      promise: input.fetchLearnedPlan(input.signal),
    });
  }

  const settled = await Promise.allSettled(tasks.map((task) => task.promise));
  if (input.signal?.aborted) {
    const abort = new DOMException('Aborted', 'AbortError');
    throw abort;
  }

  const result: ShipmentRouteCandidates = {
    google: null,
    learned: null,
    errorsByProvider: {},
  };

  settled.forEach((entry, index) => {
    const provider = tasks[index]!.provider;
    if (entry.status === 'fulfilled') {
      const plan = entry.value;
      if (provider === 'google') {
        result.google = plan;
        if (plan.meta?.error) {
          result.errorsByProvider.google = plan.meta.error;
        }
      } else {
        result.learned = plan;
        if (plan.meta?.error) {
          result.errorsByProvider.learned = plan.meta.error;
        }
      }
      return;
    }
    if (isAbortError(entry.reason)) {
      throw entry.reason;
    }
    result.errorsByProvider[provider] = providerErrorMessage(entry.reason);
  });

  return result;
}

export function resolveShipmentRouteError(
  plan: RoutePlan,
  errorsByProvider: ShipmentRouteProviderErrors,
): { error: string | null; warning: string | null } {
  const hasPaths = plan.segments.some(
    (segment) => Array.isArray(segment.paths) && segment.paths.length > 0,
  );
  const warningParts = Object.entries(errorsByProvider)
    .filter(([, message]) => Boolean(message))
    .map(([provider, message]) => `${provider}: ${message}`);
  const warning = warningParts.length ? warningParts.join(' · ') : null;

  if (hasPaths) {
    return { error: null, warning };
  }

  if (warning) {
    return { error: warning, warning: null };
  }

  return {
    error: 'No routes returned (check API key and Routes API).',
    warning: null,
  };
}

export async function runShipmentFormComputeRoute(
  ctx: ComputeRouteContext,
  deps: ComputeRouteDeps,
): Promise<void> {
  const {
    googleRouting,
    routePlan,
    routeError,
    routeWarning,
    computingRoute,
    routingLoading,
    geometryQuality,
    commitGeometryQualityImmediately,
    requestRouteOverview,
  } = deps;

  if (ctx.stopCoordinates.length < 2 && !ctx.fetchLearnedPlan) return;

  computingRoute.value = true;
  routingLoading.value = true;
  routeError.value = null;
  if (routeWarning) routeWarning.value = null;

  await yieldUntilSpinnerPainted();

  try {
    const candidates = await requestShipmentRouteCandidates({
      googleRouting,
      googleEnabled: ctx.googleEnabled !== false,
      stopCoordinates: ctx.stopCoordinates,
      fetchLearnedPlan: ctx.fetchLearnedPlan,
      signal: ctx.signal,
    });

    if (ctx.signal?.aborted) return;

    const corridorBufferM =
      ctx.corridorBufferM ?? deps.corridorBufferMDebounced.value;
    const matchThreshold = ctx.learnedGoogleDedup?.matchThreshold;
    let maxLearnedGoogleMatch: number | null = null;
    const learnedDeduped = (ctx.learnedGoogleDedup?.enabled ?? true)
      ? await filterLearnedDuplicatesAgainstGoogle(
          candidates.google,
          candidates.learned,
          corridorBufferM,
          matchThreshold,
          (evaluation) => {
            maxLearnedGoogleMatch = evaluation.maxMatch;
          },
        )
      : candidates.learned;

    if (ctx.learnedGoogleDedup?.enabled === false) {
      const evaluation = await evaluateLearnedMatchesAgainstGoogle(
        candidates.google,
        candidates.learned,
        corridorBufferM,
        matchThreshold,
      );
      maxLearnedGoogleMatch = evaluation.maxMatch;
    }

    if (ctx.signal?.aborted) return;
    ctx.onLearnedGoogleMatchMeasured?.(maxLearnedGoogleMatch);

    const merged = orderMergedSegmentPaths(
      mergeRoutePlansBySegment(candidates.google, learnedDeduped),
    );
    const resolved = resolveShipmentRouteError(merged, candidates.errorsByProvider);

    routePlan.value = merged;
    routeError.value = resolved.error;
    if (routeWarning) routeWarning.value = resolved.warning;

    const flattened = flattenRoutePlanPaths(merged);
    const pathsOk = flattened.length > 0 && resolved.error == null;

    if (pathsOk) {
      const suggestion = suggestedGeometryQualityDetailFromPaths(
        flattened.map((row) => row.path),
      );
      geometryQuality.value = suggestion.percent;
      commitGeometryQualityImmediately();
      await requestRouteOverview();
    }
  } catch (e) {
    if (isAbortError(e) || ctx.signal?.aborted) {
      return;
    }
    routeError.value = e instanceof Error ? e.message : String(e);
    routePlan.value = null;
    if (routeWarning) routeWarning.value = null;
  } finally {
    if (!ctx.signal?.aborted) {
      computingRoute.value = false;
      routingLoading.value = false;
    }
  }
}
